// client/src/services/weatherService.ts
interface WeatherApiResponse {
  location: string;
  temperature: number;
  condition: string;
  humidity: number;
  windSpeed: number;
  visibility: number;
  uvIndex: number;
  pressure: number;
  rainfall: number;
  forecast: Array<{
    day: string;
    date: string;
    high: number;
    low: number;
    condition: string;
    icon: string;
    rainfall: number;
    humidity: number;
  }>;
}

class WeatherService {
  private static instance: WeatherService;
  private cache: Map<string, { data: WeatherApiResponse; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private readonly API_TIMEOUT = 10000; // 10 seconds

  static getInstance(): WeatherService {
    if (!WeatherService.instance) {
      WeatherService.instance = new WeatherService();
    }
    return WeatherService.instance;
  }

  private async fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log('Weather API request timed out, aborting...');
      controller.abort();
    }, this.API_TIMEOUT);

    try {
      console.log(`Making weather API request to: ${url}`);
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      clearTimeout(timeoutId);
      console.log(`Weather API response received: ${response.status}`);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.error('Weather API request was aborted');
        throw new Error('Request timeout - weather service unavailable');
      }
      throw error;
    }
  }

  async getWeatherData(location: string = 'Maseru, Lesotho'): Promise<WeatherApiResponse> {
    const cacheKey = `weather_${location}`;
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('Using cached weather data');
      return cached.data;
    }

    // Try OpenWeather API directly first
    try {
      console.log('Attempting OpenWeather API call...');
      const apiKey = '736aac06406b5ae7d543c9ca81cef2a5';
        // Always prioritize GPS coordinates if available
      let apiUrl;
      if (window.gpsCoordinates) {
        const { lat, lng } = window.gpsCoordinates;
        apiUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&appid=${apiKey}&units=metric`;
        console.log('🌍 Using stored GPS coordinates for weather API:', { lat, lng });
      } else if (location.includes(',') && !location.includes(' ')) {
        const [lat, lng] = location.split(',');
        apiUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&appid=${apiKey}&units=metric`;
        console.log('Using coordinates from location string:', { lat, lng });
      } else {
        apiUrl = `https://api.openweathermap.org/data/2.5/weather?q=${encodeURIComponent(location)}&appid=${apiKey}&units=metric`;
        console.log('Using location name for weather API:', location);
      }
      
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🌤️ OpenWeather API SUCCESS - Live weather data from Lesotho:', {
          location: data.name + ', ' + data.sys.country,
          temp: Math.round(data.main.temp) + '°C',
          condition: data.weather[0].main,
          humidity: data.main.humidity + '%'
        });
        
        const weatherData: WeatherApiResponse = {
          location: data.name + ', ' + data.sys.country,
          temperature: Math.round(data.main.temp),
          condition: data.weather[0].main,
          humidity: data.main.humidity,
          windSpeed: Math.round(data.wind.speed * 3.6), // Convert m/s to km/h
          visibility: data.visibility ? Math.round(data.visibility / 1000) : 10,
          uvIndex: 6,
          pressure: data.main.pressure,
          rainfall: data.rain ? data.rain['1h'] || 0 : 0,
          forecast: await this.getForecastData(location, apiKey)
        };
        
        this.cache.set(cacheKey, { data: weatherData, timestamp: Date.now() });
        return weatherData;
      }
    } catch (error) {
      console.error('OpenWeather API failed:', error);
    }

    // Try backend API as secondary option
    try {
      console.log('Trying backend API...');
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await Promise.race([
        fetch(`${backendUrl}/api/weather-data?location=${encodeURIComponent(location)}`),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('timeout')), 5000)
        )
      ]);
      
      if (response.ok) {
        const data: WeatherApiResponse = await response.json();
        if (typeof data.temperature === 'number' && data.location && data.condition) {
          console.log('Backend API success');
          this.cache.set(cacheKey, { data, timestamp: Date.now() });
          return data;
        }
      }
    } catch (error) {
      console.error('Backend API failed:', error);
    }

    // Use cached data if available, otherwise fallback
    console.warn('⚠️ Using fallback weather data - API unavailable');
    return cached?.data || this.getFallbackWeatherData(location);
  }

  private async getForecastData(location: string, apiKey: string): Promise<WeatherApiResponse['forecast']> {
    try {
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?q=${encodeURIComponent(location)}&appid=${apiKey}&units=metric`
      );
      
      if (response.ok) {
        const data = await response.json();
        const dailyForecasts = new Map();
        
        data.list.forEach((item: any) => {
          const date = new Date(item.dt * 1000);
          const dayKey = date.toDateString();
          
          if (!dailyForecasts.has(dayKey)) {
            dailyForecasts.set(dayKey, {
              day: date.toLocaleDateString('en-US', { weekday: 'short' }),
              date: date.toLocaleDateString(),
              high: Math.round(item.main.temp_max),
              low: Math.round(item.main.temp_min),
              condition: item.weather[0].main,
              icon: item.weather[0].icon,
              rainfall: item.rain ? item.rain['3h'] || 0 : 0,
              humidity: item.main.humidity
            });
          } else {
            const existing = dailyForecasts.get(dayKey);
            existing.high = Math.max(existing.high, Math.round(item.main.temp_max));
            existing.low = Math.min(existing.low, Math.round(item.main.temp_min));
          }
        });
        
        return Array.from(dailyForecasts.values()).slice(0, 8);
      }
    } catch (error) {
      console.error('Forecast API failed:', error);
    }
    
    return this.getFallbackWeatherData(location).forecast;
  }

  private getFallbackWeatherData(location: string): WeatherApiResponse {
    return {
      location: location,
      temperature: 18,
      condition: 'Partly Cloudy',
      humidity: 65,
      windSpeed: 12,
      visibility: 10,
      uvIndex: 6,
      pressure: 1013,
      rainfall: 0,
      forecast: [
        {
          day: 'Today',
          date: new Date().toLocaleDateString(),
          high: 22,
          low: 8,
          condition: 'Partly Cloudy',
          icon: 'partly-cloudy',
          rainfall: 0,
          humidity: 65
        },
        {
          day: 'Tomorrow',
          date: new Date(Date.now() + 86400000).toLocaleDateString(),
          high: 24,
          low: 10,
          condition: 'Sunny',
          icon: 'sunny',
          rainfall: 0,
          humidity: 60
        },
        {
          day: 'Wed',
          date: new Date(Date.now() + 2 * 86400000).toLocaleDateString(),
          high: 20,
          low: 6,
          condition: 'Cloudy',
          icon: 'cloudy',
          rainfall: 10,
          humidity: 70
        },
        {
          day: 'Thu',
          date: new Date(Date.now() + 3 * 86400000).toLocaleDateString(),
          high: 26,
          low: 12,
          condition: 'Sunny',
          icon: 'sunny',
          rainfall: 0,
          humidity: 55
        },
        {
          day: 'Fri',
          date: new Date(Date.now() + 4 * 86400000).toLocaleDateString(),
          high: 28,
          low: 14,
          condition: 'Partly Cloudy',
          icon: 'partly-cloudy',
          rainfall: 5,
          humidity: 62
        },
        {
          day: 'Sat',
          date: new Date(Date.now() + 5 * 86400000).toLocaleDateString(),
          high: 25,
          low: 11,
          condition: 'Rainy',
          icon: 'rainy',
          rainfall: 80,
          humidity: 85
        },
        {
          day: 'Sun',
          date: new Date(Date.now() + 6 * 86400000).toLocaleDateString(),
          high: 23,
          low: 9,
          condition: 'Cloudy',
          icon: 'cloudy',
          rainfall: 20,
          humidity: 75
        },
        {
          day: 'Mon',
          date: new Date(Date.now() + 7 * 86400000).toLocaleDateString(),
          high: 27,
          low: 13,
          condition: 'Sunny',
          icon: 'sunny',
          rainfall: 0,
          humidity: 58
        }
      ]
    };
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const weatherService = WeatherService.getInstance();
export type { WeatherApiResponse };